import { useImmerAtom } from 'jotai-immer';
import { ListTableStateHookReturn, TblPagination, TblSortDir } from '../../../core/table/table.types';
import { useCallback, useEffect } from 'react';
import { tblSpecSetFilter, tblSpecSetSort } from '../../../core/table/spec';
import {
    cpOrderInvoiceDetailsTableFiltersAtom,
    cpOrderInvoiceDetailsTableStateAtom,
    cpOrderInvoiceDetailsTableStatsAtom,
    cpOrderInvoiceDetailsTableViewAtom,
    cpOrderInvoicesDetailsPageStateAtom,
} from './cp_order_invoices_details_atoms';

export const useCpOrderInvoiceLineItemsTableState = (): ListTableStateHookReturn => {
    const [state, _setState] = useImmerAtom(cpOrderInvoiceDetailsTableStateAtom);
    const [view, setView] = useImmerAtom(cpOrderInvoiceDetailsTableViewAtom);
    const [filters, setFilters] = useImmerAtom(cpOrderInvoiceDetailsTableFiltersAtom);
    const [stats, setStats] = useImmerAtom(cpOrderInvoiceDetailsTableStatsAtom);
    const [detailsPageState] = useImmerAtom(cpOrderInvoicesDetailsPageStateAtom);

    const setPaging = useCallback(
        (paging: Partial<TblPagination>): void => {
            setStats(draft => {
                if (paging.page !== undefined) {
                    draft.paging.page = paging.page;
                }
                if (paging.size !== undefined) {
                    draft.paging.size = paging.size;
                }
                if (paging.total !== undefined) {
                    draft.paging.total = paging.total;
                }
            });
        },
        [setStats],
    );

    const setSort = useCallback(
        (col: string, dir?: TblSortDir): void => {
            setView(draft => {
                draft.sort = tblSpecSetSort(col, view.cols, dir);
            });
        },
        [setView, view.cols],
    );

    const setFilter = useCallback(
        (key: string, val: any): void => {
            setFilters(draft => tblSpecSetFilter(draft, key, view.cols, val));
        },
        [setFilters, view.cols],
    );

    // No-op: waiting is owned by useFetchInvoice via the page state atom.
    // Retained to satisfy ListTableStateHookReturn type contract.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const setWait = useCallback((_waiting: boolean): void => {}, []);

    useEffect(() => {
        if (detailsPageState.rebatesOnly) {
            setFilter('IsRebate', 'YES');
        }
    }, [detailsPageState, setFilter]);

    return {
        waiting: detailsPageState.waiting,
        views: state.views,
        currentViewId: state.currentViewId,
        cols: view.cols,
        sections: view.sections,
        sort: view.sort,
        filters: filters,
        fltStats: stats.fltStats,
        paging: stats.paging,
        setWait,
        setSort,
        setFilter,
        setPaging,
    };
};
